<template>
  <div>
    <sidebar></sidebar>
    <div :style="{ 'margin-left': sidebarwidth }">
      <div class="max-w-lg mx-auto px-5 py-10">
        <div class="bg-white rounded-lg shadow p-8">
          <h2 class="text-2xl font-bold mb-1">My Profile</h2>
          <p class="text-sm text-gray-500 mb-6">Update your personal information.</p>

          <div v-if="loadingProfile" class="py-10 text-center text-gray-400">Loading...</div>

          <div v-else>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                v-model="form.first_name"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                v-model="form.last_name"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                :value="form.email"
                type="email"
                disabled
                class="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
              />
              <p class="text-xs text-gray-400 mt-1">Email cannot be changed here.</p>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                v-model="form.phone"
                type="tel"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                v-model="form.department"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-1">Designation</label>
              <input
                v-model="form.designation"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <p v-if="errorMsg"   class="text-red-500 text-sm mb-4">{{ errorMsg }}</p>
            <p v-if="successMsg" class="text-green-600 text-sm mb-4">{{ successMsg }}</p>

            <div class="flex gap-3">
              <button
                @click="saveProfile"
                :disabled="saving"
                class="flex-1 bg-black text-white font-bold py-2 rounded hover:bg-gray-800 disabled:opacity-60"
              >{{ saving ? 'Saving...' : 'Save Changes' }}</button>
              <router-link
                to="/change-password"
                class="flex-1 text-center border border-gray-300 text-sm font-medium py-2 rounded hover:bg-gray-50"
              >Change Password</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import sidebar from "@/components/sidebar/Sidebar.vue";
import { sidebarwidth, setCurrentUser } from "@/store/index.js";
import api from "../../boot/axios.js";

const form = ref({
  first_name:  "",
  last_name:   "",
  email:       "",
  phone:       "",
  department:  "",
  designation: "",
});

const loadingProfile = ref(true);
const saving         = ref(false);
const errorMsg       = ref("");
const successMsg     = ref("");

const getDisplayName = (firstName, lastName) => [firstName, lastName].filter(Boolean).join(" ").trim();

// Load current user data
onMounted(async () => {
  try {
    const res = await api.get("/auth/currentuser");
    const u   = res.data;
    form.value = {
      first_name:  u.first_name  || "",
      last_name:   u.last_name   || "",
      email:       u.email       || "",
      phone:       u.phone       || "",
      department:  u.department  || "",
      designation: u.designation || "",
    };
    setCurrentUser.value = getDisplayName(u.first_name, u.last_name) || u.email || "User";
    localStorage.setItem("name", setCurrentUser.value);
  } catch (err) {
    errorMsg.value = "Failed to load profile.";
  } finally {
    loadingProfile.value = false;
  }
});

const saveProfile = async () => {
  errorMsg.value   = "";
  successMsg.value = "";
  saving.value     = true;

  try {
    const res = await api.patch("/auth/update-profile", {
      first_name:  form.value.first_name,
      last_name:   form.value.last_name,
      phone:       form.value.phone,
      department:  form.value.department,
      designation: form.value.designation,
    });

    if (res.data.status === true) {
      successMsg.value = "Profile updated successfully.";
      const displayName = getDisplayName(form.value.first_name, form.value.last_name) || form.value.email || "User";
      setCurrentUser.value = displayName;
      localStorage.setItem("name", displayName);
    } else {
      errorMsg.value = res.data.message || "Update failed.";
    }
  } catch (err) {
    errorMsg.value = err?.response?.data?.message || "Something went wrong.";
  } finally {
    saving.value = false;
  }
};
</script>